// S9 — health monitoring API client + react-query hooks.
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

type Ts = { utc: string; local: string; timezone: string };

export interface HealthSummary {
  total_cameras: number;
  online_count: number;
  offline_count: number;
  unknown_count: number;
  recording_count: number;
  not_recording_count: number;
  active_alerts_by_severity: { critical: number; warning: number; info: number };
  active_alert_count: number;
  overall_uptime_pct: number;
  overall_recording_pct: number;
  cameras_with_critical_alerts: string[];
}

export interface CameraHealthRow {
  camera_id: string;
  name: string;
  entity_name: string | null;
  /** AUTHORITATIVE. `current_status` is a legacy mirror with five writers,
   *  only one of which keeps the pair in lockstep - do not bind UI to it. */
  status: string;
  current_status: "online" | "offline" | "unknown";
  is_recording: boolean;
  last_health_check_at: Ts | null;
  consecutive_failures: number;
  active_alert_count: number;
  // Batch 1 (UX/IA) — NVR hierarchy context (DeviceContextLabel).
  site_name?: string | null;
  nvr_make?: string | null;
  nvr_model?: string | null;
  nvr_serial?: string | null;
  channel_no?: number | null;
}

export interface CameraHealthDetail {
  camera_id: string;
  name: string;
  entity_name: string | null;
  /** AUTHORITATIVE - see above. */
  status: string;
  current_status: string;
  consecutive_failures: number;
  last_health_check_at: Ts | null;
  today_uptime_pct: number;
  seven_day_trend: Array<{ date: string; uptime_pct: number }>;
  latest: Record<string, unknown> | null;
  active_alerts: Array<{
    id: string;
    alert_type: string;
    severity: string;
    message: string;
    first_seen_at: Ts | null;
    last_seen_at: Ts | null;
    occurrence_count: number;
  }>;
  recording_compliance_today_pct: number | null;
}

export interface HealthLogItem {
  checked_at: Ts | null;
  is_online: boolean;
  is_recording: boolean;
  bitrate_kbps: number | null;
  fps: number | null;
  snapshot_ok: boolean | null;
  hdd_used_pct: number | null;
  error_message: string | null;
}

export interface NvrHealthItem {
  agent_id: string | null;
  device_name: string | null;
  ip_address: string | null;
  is_online: boolean;
  hdd_count: number;
  hdd_total_gb: number | null;
  hdd_used_gb: number | null;
  hdd_used_pct: number | null;
  hdd_status: "normal" | "warning" | "critical" | "unknown";
  channel_count: number;
  recording_channel_count: number;
  firmware_version: string | null;
  uptime_seconds: number | null;
  checked_at: Ts | null;
}

export interface HealthAlertItem {
  id: string;
  camera_id: string | null;
  alert_type: string;
  severity: "info" | "warning" | "critical";
  message: string;
  occurrence_count: number;
  resolved: boolean;
  auto_resolved: boolean;
  first_seen_at: Ts | null;
  last_seen_at: Ts | null;
  resolved_at: Ts | null;
}

export interface ExceptionReportRow {
  camera_id: string;
  name: string;
  entity_name: string | null;
  uptime_pct: number;
  recording_pct: number;
  issues: string[];
}

export interface EntityHealthRollup {
  entity_id: string;
  total_cameras: number;
  cameras_online: number;
  cameras_recording: number;
  cameras_with_alerts: number;
  avg_uptime_pct: number;
  avg_recording_pct: number;
  compliance_score: number;
}


// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------
export function useHealthSummary(refetchMs = 60_000) {
  return useQuery({
    queryKey: ["health", "summary"],
    queryFn: async () => {
      const { data } = await api.get<HealthSummary>("/api/v1/health/summary");
      return data;
    },
    refetchInterval: refetchMs,
  });
}

export function useHealthCameras(params?: {
  entity_id?: string;
  status?: string;
  severity?: string;
}) {
  return useQuery({
    queryKey: ["health", "cameras", params ?? {}],
    queryFn: async () => {
      const { data } = await api.get<{ items: CameraHealthRow[]; total: number }>(
        "/api/v1/health/cameras",
        { params },
      );
      return data;
    },
    refetchInterval: 30_000,
  });
}

export function useCameraHealthDetail(cameraId: string | null) {
  return useQuery({
    queryKey: ["health", "camera-detail", cameraId],
    queryFn: async () => {
      const { data } = await api.get<CameraHealthDetail>(
        `/api/v1/health/cameras/${cameraId}`,
      );
      return data;
    },
    enabled: Boolean(cameraId),
    refetchInterval: 30_000,
  });
}

export function useCameraHealthHistory(
  cameraId: string | null,
  limit = 100,
) {
  return useQuery({
    queryKey: ["health", "history", cameraId, limit],
    queryFn: async () => {
      const { data } = await api.get<{ items: HealthLogItem[]; total: number }>(
        `/api/v1/health/cameras/${cameraId}/history`,
        { params: { limit } },
      );
      return data;
    },
    enabled: Boolean(cameraId),
  });
}

export function useNvrHealthList() {
  return useQuery({
    queryKey: ["health", "nvr"],
    queryFn: async () => {
      const { data } = await api.get<NvrHealthItem[]>("/api/v1/health/nvr");
      return data;
    },
    refetchInterval: 60_000,
  });
}

export function useHealthAlerts(params?: {
  camera_id?: string;
  severity?: string;
  alert_type?: string;
}) {
  return useQuery({
    queryKey: ["health", "alerts", params ?? {}],
    queryFn: async () => {
      const { data } = await api.get<{ items: HealthAlertItem[]; total: number }>(
        "/api/v1/health/alerts",
        { params },
      );
      return data;
    },
    refetchInterval: 60_000,
  });
}

export function useExceptionReport(params?: {
  uptime_threshold?: number;
  recording_threshold?: number;
  entity_id?: string;
}) {
  return useQuery({
    queryKey: ["health", "exception", params ?? {}],
    queryFn: async () => {
      const { data } = await api.get<{ items: ExceptionReportRow[]; total: number }>(
        "/api/v1/health/exception-report",
        { params },
      );
      return data;
    },
  });
}


// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------
export function usePollCamera() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cameraId: string) => {
      const { data } = await api.post<{ status: string }>(
        `/api/v1/health/cameras/${cameraId}/poll`,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["health"] });
    },
  });
}

export function useResolveAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (alertId: string) => {
      const { data } = await api.post<HealthAlertItem>(
        `/api/v1/health/alerts/${alertId}/resolve`,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["health"] });
    },
  });
}
